import React, { useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getYear, getMonth } from 'date-fns';
import {
  Box,
  Button,
  FlexBox,
  FormDialog,
  Grid,
  Icon,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
  useTheme
} from '@kis-technologies/kis-ui';
import { AdminWithActivity } from 'store/admins';
import { Kiosk } from 'store/kiosks';
import { getSortedList } from 'utils/stringFormat';
import { useStyles } from '../styled';
import { FiltersOverlayProps } from '../types';

export const FiltersOverlay = (props: FiltersOverlayProps) => {
  const {
    isOpen,
    onClose,
    kioskIdsToFilter,
    setKioskIdsToFilter,
    usersToFilter,
    setUsersToFilter,
    selectedStartDate,
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,
    filterChips,
    setFilterChips,
    kioskList,
    admins,
    handleFilter
  } = props;
  const classes = useStyles({ isOpen })();
  const theme = useTheme();

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  useEffect(() => {
    if (!isOpen) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectionChange = (selectionType: string, event: any) => {
    const {
      target: { value }
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;

    if (selectionType === 'kiosk') setKioskIdsToFilter(newValue);
    else if (selectionType === 'user') setUsersToFilter(newValue);
  };

  const onApply = () => {
    const data: string[] = [];
    if (kioskIdsToFilter.length > 0) data.push('Kiosk');
    if (usersToFilter.length > 0) data.push('User');
    if (selectedStartDate) data.push('Start Date Time');
    if (selectedEndDate) data.push('End Date Time');

    setFilterChips(data);
    handleFilter();
    onClose();
  };

  const onCancel = () => {
    if (filterChips && filterChips.length === 0) {
      resetFilters();
    }
    onClose();
  };

  const resetFilters = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setKioskIdsToFilter([]);
    setUsersToFilter([]);
  };

  const getStyles = (option: string, selectedOptions: string[]) => {
    return {
      fontWeight:
        selectedOptions.indexOf(option) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium
    };
  };

  const datePickerCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }: any) => (
    <div
      style={{
        margin: 10,
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <button
        style={{ marginRight: '15px', color: '#F46D2E' }}
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      >
        {'<'}
      </button>
      <select
        style={{ marginRight: '15px', fontFamily: 'inherit' }}
        value={getYear(date)}
        onChange={({ target: { value } }) => changeYear(parseInt(value))}
      >
        {years.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <select
        style={{ marginRight: '15px', fontFamily: 'inherit' }}
        value={months[getMonth(date)]}
        onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
      >
        {months.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <button style={{ color: '#F46D2E' }} onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
        {'>'}
      </button>
    </div>
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DatePickerCustomInput = React.forwardRef((props1: any, ref: any) => {
    return (
      <div
        onClick={props1.onClick}
        ref={ref}
        className={classes.datePickerInput}
        style={{ padding: props1.value ? '0 40px 0 12px' : '0 12px' }}
      >
        <label>{props1.value || props1.placeholder}</label>
        <Icon name="CalendarToday" sx={{ color: '#F46D2E' }} />
      </div>
    );
  });

  const disableButton = !selectedStartDate && !selectedEndDate && !kioskIdsToFilter[0] && !usersToFilter[0];

  return (
    <FormDialog
      cancelButtonAction={onCancel}
      cancelButtonText="Cancel"
      confirmButtonAction={onApply}
      confirmButtonText="Apply"
      open={isOpen}
      title="Filters"
    >
      <Grid direction="column" width="100%">
        <FlexBox className={classes.filtersFieldsFlexBox}>
          {/* Start time filter */}
          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>Start Date and Time</Typography>
            <div className="my-datepicker-wrapper">
              <div className={classes.datePickerContainer}>
                <DatePicker
                  showTimeSelect
                  renderCustomHeader={datePickerCustomHeader}
                  todayButton={'Today'}
                  placeholderText={'MM/DD/YYYY hh:mm aa'}
                  selected={selectedStartDate}
                  onChange={(date: Date | null) => {
                    setSelectedStartDate(date);
                  }}
                  isClearable
                  dateFormat="MM/dd/yyyy h:mm aa"
                  className={classes.datePickerComponent}
                  customInput={<DatePickerCustomInput />}
                  calendarClassName="my-custom-calendar"
                />
              </div>
            </div>
          </Box>
          {/* End time filter */}
          <Box className={classes.filtersFieldBox}>
            <Typography className={classes.filtersFieldLabel}>End Date and Time</Typography>
            <div className="my-datepicker-wrapper">
              <div className={`${classes.datePickerContainer} ${classes.rightDatePickerContainer}`}>
                <DatePicker
                  showTimeSelect
                  renderCustomHeader={datePickerCustomHeader}
                  todayButton={'Today'}
                  placeholderText={'MM/DD/YYYY hh:mm aa'}
                  selected={selectedEndDate}
                  onChange={(date: Date | null) => {
                    setSelectedEndDate(date);
                  }}
                  isClearable
                  dateFormat="MM/dd/yyyy h:mm aa"
                  className={classes.datePickerComponent}
                  customInput={<DatePickerCustomInput />}
                  calendarClassName="my-custom-calendar"
                />
              </div>
            </div>
          </Box>
        </FlexBox>
        {/* Kiosk names filter */}
        <Box className={classes.filtersFieldBox}>
          <Typography className={classes.filtersFieldLabel}>Kiosk</Typography>
          <Select
            className={classes.filtersInputField}
            multiple
            displayEmpty
            value={kioskIdsToFilter}
            onChange={(e) => handleSelectionChange('kiosk', e)}
            input={<OutlinedInput />}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderValue={(selected: any) => {
              if (selected.length === 0) {
                return <Typography>Select Kiosk(s)</Typography>;
              }
              return selected
                .map((selectedId: string) => {
                  const selectedKiosk = kioskList.find((k: Kiosk) => k.id === selectedId);
                  return selectedKiosk ? selectedKiosk.name : '';
                })
                .join(', ');
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 224,
                  width: 250
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
            inputProps={{ 'aria-label': 'Without label' }}
          >
            <MenuItem disabled value="">
              <Typography>Select Kiosk(s)</Typography>
            </MenuItem>
            {getSortedList(kioskList, 'name').map((kiosk: Kiosk) => (
              <MenuItem key={kiosk.id} value={kiosk.id} sx={getStyles(kiosk.id, kioskIdsToFilter)}>
                {kiosk.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {/* User filter */}
        <Box className={classes.filtersFieldBox}>
          <Typography className={classes.filtersFieldLabel}>User</Typography>
          <Select
            className={classes.filtersInputField}
            multiple
            displayEmpty
            value={usersToFilter}
            onChange={(e) => handleSelectionChange('user', e)}
            input={<OutlinedInput />}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            renderValue={(selected: any) => {
              if (selected.length === 0) {
                return <Typography>Select User(s)</Typography>;
              }

              return selected.join(', ');
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 224,
                  width: 250
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left'
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left'
              }
            }}
            inputProps={{ 'aria-label': 'Without label' }}
          >
            <MenuItem disabled value="">
              <Typography>Select User(s)</Typography>
            </MenuItem>
            {getSortedList(admins, 'name').map((admin: AdminWithActivity) => (
              <MenuItem key={admin.id} value={admin.name} sx={getStyles(admin.name, usersToFilter)}>
                {admin.name}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {/* Reset button */}
        <FlexBox className={classes.filtersResetButtonBox}>
          <Button
            customSize="small"
            width={200}
            disabled={disableButton ? true : false}
            onClick={resetFilters}
            data-testid="reset-filters-button"
          >
            Reset
          </Button>
        </FlexBox>
      </Grid>
    </FormDialog>
  );
};
